// src/pages/admin/CoverLetterAccessManagement.tsx
import React, { useEffect, useState } from "react";
import {
  FaFileAlt,
  FaSync,
  FaCheck,
  FaTimes,
  FaEdit,
  FaChartBar,
} from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

type StudentAccess = {
  studentId: number;
  studentName: string;
  email: string;
  hasAccess: boolean;
  hasAusbildungAccess: boolean;
  hasFSJAccess: boolean;
  // Ausbildung
  usageCount: number;
  maxGenerations: number | null;
  lastUsedAt: string | null;
  // FSJ
  usageCountFSJ: number;
  maxGenerationsFSJ: number | null;
  lastUsedAtFSJ: string | null;
  accessGrantedAt: string | null;
  grantedByName: string | null;
};

type Stats = {
  totalStudentsWithAccess: number;
  totalGenerations: number;
  totalAccessRecords: number;
};

type EditingLimit = { studentId: number; type: "ausbildung" | "fsj" };

const CoverLetterAccessManagement: React.FC = () => {
  const [students, setStudents] = useState<StudentAccess[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<StudentAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAccess, setFilterAccess] = useState<"all" | "ausbildung" | "fsj" | "any" | "none">("all");
  const [stats, setStats] = useState<Stats | null>(null);
  const [editingLimit, setEditingLimit] = useState<EditingLimit | null>(null);
  const [limitValue, setLimitValue] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [accessRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/cover-letter/access`, { credentials: "include" }),
        fetch(`${API_BASE}/api/admin/cover-letter/stats`, { credentials: "include" }),
      ]);
      if (!accessRes.ok) throw new Error("Failed to load access data");
      const accessData: StudentAccess[] = await accessRes.json();
      setStudents(accessData);
      setFilteredStudents(accessData);
      if (statsRes.ok) setStats(await statsRes.json());
      setLastUpdated(new Date());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toggleAusbildung = async (studentId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/cover-letter/toggle-ausbildung/${studentId}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      loadData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to toggle Ausbildung access");
    }
  };

  const toggleFSJ = async (studentId: number) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/cover-letter/toggle-fsj/${studentId}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      loadData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to toggle FSJ access");
    }
  };

  const updateLimit = async (studentId: number, type: "ausbildung" | "fsj") => {
    try {
      const maxGen = limitValue === "" ? null : parseInt(limitValue);
      const endpoint = type === "fsj" ? "limit-fsj" : "limit";
      const res = await fetch(
        `${API_BASE}/api/admin/cover-letter/${endpoint}/${studentId}?maxGenerations=${maxGen}`,
        { method: "PUT", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed");
      setEditingLimit(null);
      setLimitValue("");
      loadData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to update limit");
    }
  };

  const resetUsage = async (studentId: number, type: "ausbildung" | "fsj", name: string) => {
    if (!window.confirm(`Reset ${type === "fsj" ? "FSJ" : "Ausbildung"} usage for ${name}?`)) return;
    try {
      const endpoint = type === "fsj" ? "reset-usage-fsj" : "reset-usage";
      const res = await fetch(`${API_BASE}/api/admin/cover-letter/${endpoint}/${studentId}`, {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      loadData();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to reset usage");
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    let filtered = students;
    if (filterAccess === "ausbildung") filtered = filtered.filter((s) => s.hasAusbildungAccess);
    else if (filterAccess === "fsj") filtered = filtered.filter((s) => s.hasFSJAccess);
    else if (filterAccess === "any") filtered = filtered.filter((s) => s.hasAusbildungAccess || s.hasFSJAccess);
    else if (filterAccess === "none") filtered = filtered.filter((s) => !s.hasAusbildungAccess && !s.hasFSJAccess);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (s) => s.studentName.toLowerCase().includes(term) || s.email.toLowerCase().includes(term)
      );
    }
    setFilteredStudents(filtered);
    setCurrentPage(1);
  }, [students, searchTerm, filterAccess]);

  const totalItems = filteredStudents.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedStudents = filteredStudents.slice(startIndex, startIndex + pageSize);

  const AccessBadge = ({
    granted, onToggle,
  }: { granted: boolean; onToggle: () => void }) => (
    <button
      onClick={onToggle}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
        granted ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-red-100 text-red-800 hover:bg-red-200"
      }`}
    >
      {granted ? <><FaCheck size={10} />Granted</> : <><FaTimes size={10} />Denied</>}
    </button>
  );

  const LimitCell = ({
    studentId, type, usage, limit, name,
  }: { studentId: number; type: "ausbildung" | "fsj"; usage: number; limit: number | null; name: string }) => {
    const isEditing = editingLimit?.studentId === studentId && editingLimit?.type === type;
    return (
      <div className="text-center">
        <div className="text-sm font-semibold text-gray-900">{usage}</div>
        {usage > 0 && (
          <button onClick={() => resetUsage(studentId, type, name)} className="text-xs text-blue-600 hover:text-blue-800">Reset</button>
        )}
        {isEditing ? (
          <div className="flex items-center justify-center gap-1 mt-1">
            <input
              type="number" value={limitValue} onChange={(e) => setLimitValue(e.target.value)}
              placeholder="∞" className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
              min="1" style={{ fontSize: "14px" }}
            />
            <button onClick={() => updateLimit(studentId, type)} className="text-green-600 hover:text-green-800"><FaCheck size={12} /></button>
            <button onClick={() => { setEditingLimit(null); setLimitValue(""); }} className="text-red-600 hover:text-red-800"><FaTimes size={12} /></button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className="text-xs text-gray-500">/ {limit ?? "∞"}</span>
            <button
              onClick={() => { setEditingLimit({ studentId, type }); setLimitValue(limit?.toString() ?? ""); }}
              className="text-blue-600 hover:text-blue-800"
            >
              <FaEdit size={11} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaFileAlt />}
        title="Cover Letter Access Management"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Cover Letter Access" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button onClick={loadData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50">
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p className="font-semibold">Error</p><p>{error}</p>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {[
              { label: "Students with Access", value: stats.totalStudentsWithAccess, icon: <FaCheck className="text-blue-600 text-xl" />, bg: "from-blue-50 to-cyan-50 border-blue-200" },
              { label: "Total Generations", value: stats.totalGenerations, icon: <FaFileAlt className="text-green-600 text-xl" />, bg: "from-green-50 to-emerald-50 border-green-200" },
              { label: "Access Records", value: stats.totalAccessRecords, icon: <FaChartBar className="text-purple-600 text-xl" />, bg: "from-purple-50 to-pink-50 border-purple-200" },
            ].map((s) => (
              <div key={s.label} className={`bg-gradient-to-br ${s.bg} border rounded-xl p-5 shadow-sm`}>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/60 rounded-lg flex items-center justify-center">{s.icon}</div>
                  <div>
                    <p className="text-sm text-gray-600 font-medium">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search Students</label>
              <input type="text" placeholder="Search by name or email..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" style={{ fontSize: "16px" }} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter by Access</label>
              <select value={filterAccess} onChange={(e) => setFilterAccess(e.target.value as typeof filterAccess)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent" style={{ fontSize: "16px" }}>
                <option value="all">All Students</option>
                <option value="any">Has Any Access</option>
                <option value="ausbildung">Ausbildung Access</option>
                <option value="fsj">FSJ/BFD/FÖJ Access</option>
                <option value="none">No Access</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12 text-[#009fe3] flex items-center justify-center gap-2">
              <FaSync className="animate-spin" /><span>Loading students...</span>
            </div>
          ) : paginatedStudents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No students found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-[#009fe3] text-white">
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Student</th>
                    <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                    {/* Ausbildung */}
                    <th className="px-4 py-4 text-center text-sm font-semibold bg-[#007cb5]">
                      <div>🏢 Ausbildung</div>
                      <div className="text-xs font-normal opacity-90">Access</div>
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold bg-[#007cb5]">
                      <div>🏢 Ausbildung</div>
                      <div className="text-xs font-normal opacity-90">Used / Limit</div>
                    </th>
                    {/* FSJ */}
                    <th className="px-4 py-4 text-center text-sm font-semibold bg-[#005f8e]">
                      <div>🤝 FSJ/BFD/FÖJ</div>
                      <div className="text-xs font-normal opacity-90">Access</div>
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold bg-[#005f8e]">
                      <div>🤝 FSJ/BFD/FÖJ</div>
                      <div className="text-xs font-normal opacity-90">Used / Limit</div>
                    </th>
                    <th className="px-4 py-4 text-center text-sm font-semibold">Last Used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedStudents.map((student, idx) => (
                    <tr key={student.studentId}
                      className={`hover:bg-blue-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                      <td className="px-4 py-4">
                        <div className="font-semibold text-gray-900">{student.studentName}</div>
                        <div className="text-xs text-gray-500">ID: {student.studentId}</div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{student.email}</td>

                      {/* Ausbildung access */}
                      <td className="px-4 py-4 text-center bg-blue-50/30">
                        <AccessBadge granted={student.hasAusbildungAccess} onToggle={() => toggleAusbildung(student.studentId)} />
                      </td>
                      <td className="px-4 py-4 bg-blue-50/30">
                        <LimitCell
                          studentId={student.studentId} type="ausbildung"
                          usage={student.usageCount ?? 0} limit={student.maxGenerations}
                          name={student.studentName}
                        />
                      </td>

                      {/* FSJ access */}
                      <td className="px-4 py-4 text-center bg-indigo-50/30">
                        <AccessBadge granted={student.hasFSJAccess} onToggle={() => toggleFSJ(student.studentId)} />
                      </td>
                      <td className="px-4 py-4 bg-indigo-50/30">
                        <LimitCell
                          studentId={student.studentId} type="fsj"
                          usage={student.usageCountFSJ ?? 0} limit={student.maxGenerationsFSJ}
                          name={student.studentName}
                        />
                      </td>

                      <td className="px-4 py-4 text-center text-xs text-gray-500">
                        {student.lastUsedAt ? (
                          <div>A: {new Date(student.lastUsedAt).toLocaleDateString()}</div>
                        ) : null}
                        {student.lastUsedAtFSJ ? (
                          <div>F: {new Date(student.lastUsedAtFSJ).toLocaleDateString()}</div>
                        ) : null}
                        {!student.lastUsedAt && !student.lastUsedAtFSJ && "Never"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(startIndex + pageSize, totalItems)} of {totalItems}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Per page:</label>
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-[#009fe3]">
                    {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">Previous</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p = totalPages <= 5 ? i + 1 : currentPage <= 3 ? i + 1 : currentPage >= totalPages - 2 ? totalPages - 4 + i : currentPage - 2 + i;
                    return (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === p ? "bg-[#009fe3] text-white" : "border border-gray-300 hover:bg-gray-50"}`}>
                        {p}
                      </button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50">Next</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CoverLetterAccessManagement;
